import ReactDOM from 'react-dom';
import React from 'react';
import FullCalendar, {
  EventClickArg,
  EventContentArg,
  DayCellContentArg,
  CalendarApi,
  EventApi,
  DateInput,
  EventSourceInput,
  DatesSetArg,
} from '@fullcalendar/react';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, CircularProgress } from '@mui/material';
import {
  CalendarModalsType,
  ProviderMedicalRole,
  NEW_EVENT_ID,
  CalendarViewTypes,
  TimeRange,
  WeekDay,
  WeekDayKeys,
} from '@uplift/types';
import { CalendarDateService, DateObject, TimeWindow } from '@uplift/services';
import { EventItem, EventItemCreated } from '../EventItem';

import { CalendarTypeSelect, useAvailableCalendarTypes } from './calendar-controls/calendar-type-select';

import { CalendarEventInput } from '@uplift/store';
import { UpliftTZPlugin } from './uplift-tz-plugin';
import { DayHeaderContent } from './ScheduleCalendarDayHeaderContent';
import { titleFormatter } from './ScheduleCalendarTitleFormatter';
import { NotAllowedArea } from './NotAllowedArea';
import { scrollCalendarToCurrentTime } from './ScheduleCalendar.helpers';
import { useStyles } from './ScheduleCalendar.styles';
import { useCurrentuser, useTimezone } from '@uplift/contexts';

import EventEmitter from 'events';


import { UpliftCalendarStylesWrapper } from '../UpliftCalendarStylesWrapper';
import { EditingAvailabilityDay, EditingAvailabilityDayPopup, LiveAvailabilityData } from '../AvailabilityCalendar/EditingAvailabilityDayPopup';

import { DateFormats } from '@uplift/constants';
import { Alert } from '../../Alert';
import { AvailabilitySettings } from '../AvailabilitySettings';
import { ClosableMenu, ClosableMenuItem } from '../../lib/simple-menu';
import { useMousePositionedPopover } from './MousePositionedPopover.hook';
import { ICSEvent, ICSEventConflict } from '@uplift/api';
import { ICSEventDetailsPopup } from './ICSEventDetailsPopup.component';
import { ICSConflictsButton } from './ICSConflictsButton.component';


export type CalendarRef = {
  editAvailability: (date: DateObject, specific?: boolean) => void;
  customEmitter: EventEmitter;
  calendarApi?: CalendarApi;
};

export type CalendarNewEvent = {
  id: string;
  start: string;
  end: string;
};

export type ScheduleCalendarProps = {
  newEventAnchorRef?: (anchor: HTMLDivElement) => void;

  calendarApi?: CalendarApi;
  setCalendarApi: (api: CalendarApi) => void;
  openModal: (modalName: keyof CalendarModalsType) => void;
  setSelectedEvent: (event: Pick<EventApi, 'start' | 'end'>) => void;
  availableSlots: Set<string>;
  checkSlotAvailability(start: DateObject, end: DateObject, slotMinutesDuration: number): boolean;
  isCalendarLoading: boolean;
  events?: CalendarEventInput[];
  availabilityTemplate?: Record<WeekDayKeys, TimeRange[]>;
  overrides?: Record<string, TimeRange[]>;
  loadAllEvents?: (info: {
    start: Date;
    end: Date;
    startStr: string;
    endStr: string;
    timeZone: string;
  }) => void;
  now?: DateInput | (() => DateInput);
  onAvailabilityDateOverride(date: DateObject, ranges: TimeRange[]): Promise<void>;
  availabilityEditing?: boolean;
  calendarRef?: React.MutableRefObject<CalendarRef | undefined>;
  setNewEvent?: (event: CalendarNewEvent) => void;
  icsEvents?: ICSEvent[];
  icsConflicts?: ICSEventConflict[];
  onICSConflictsOpen?: () => void;
  allowToCreateAppointments?: boolean;
};


const renderDayNotAllowedArea = (date: DateObject) => {
  const height = date.hour() * 60 + date.minute();
  const wrapper = document.querySelector('.day-cell__today');

  if (!wrapper) {
    return;
  }

  return ReactDOM.createPortal(<NotAllowedArea height={height} />, wrapper);
};


export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = React.memo(({
  calendarApi,
  calendarRef: _calendarRef,
  availabilityTemplate,
  setNewEvent,
  overrides = {},
  events = [],
  icsEvents: _icsEvents = [],
  icsConflicts = [],
  onICSConflictsOpen,
  allowToCreateAppointments,
  ...props
}) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const availableCalendarTypes = useAvailableCalendarTypes();
  const [ calendarType, setCalendarType ] = React.useState(() => {
    const predefined = availableCalendarTypes.find(av => av.value === CalendarViewTypes.timeGridWeek);

    if (predefined) {
      return predefined;
    }

    return availableCalendarTypes[0];
  });

  const [ liveAvailabilityData, _setLiveAvailabilityData ] = React.useState<LiveAvailabilityData>();
  const setLiveAvailabilityData = (e?: LiveAvailabilityData, force = false) => {
    force ? _setLiveAvailabilityData(e) : setTimeout(() => {
      _setLiveAvailabilityData(e);
    }, 100);
  };

  const [ currentDates, setCurrentDates ] = React.useState<DatesSetArg>();
  const [ editingAvailabilityTemplate, setEditingAvailabilityTemplate ] = React.useState<EditingAvailabilityDay>();

  React.useEffect(() => {
    if (currentDates) {
      props.loadAllEvents?.(currentDates);
    }
  }, [ currentDates ]); // eslint-disable-line react-hooks/exhaustive-deps

  const icsEvents = React.useMemo(() => {
    return _icsEvents?.map(e => ({
      id: e.id,
      title: e.title,
      start: e.fromDatetime,
      end: e.toDatetime,
      display: 'ics',
      allDay: e.allDay,
      extendedProps: {
        variant: 'ics',
        icsEvent: e,
        duration: CalendarDateService.date(e.toDatetime).diff(e.fromDatetime, 'minutes')
      }
    })) ?? [];
  }, [ _icsEvents ]);

  const availabilityEvents = React.useMemo(() => {
    if (!currentDates?.startStr || !currentDates?.endStr) return [];
    const liveDateKey = liveAvailabilityData?.date?.format(DateFormats.ISO_DATE);
    const start = CalendarDateService.date(currentDates.startStr).tz(currentDates.timeZone);
    const end = CalendarDateService.date(currentDates.endStr).tz(currentDates.timeZone);
    const range = CalendarDateService.range(start, end, 1, 'day');

    const availability = range.flatMap(day => {
      const dateKey = day.format(DateFormats.ISO_DATE);
      const live = liveDateKey === dateKey && liveAvailabilityData?.ranges
        ? CalendarDateService.squashTimeRanges(liveAvailabilityData.ranges)
        : undefined;
      const dateOverride = overrides[dateKey];
      const template = availabilityTemplate?.[WeekDay[day.day()] as WeekDayKeys];
      const ranges = live ?? dateOverride ?? template ?? [];

      return ranges.map(range => {
        const start = CalendarDateService.time(range.start, { base: day }).tz(currentDates.timeZone, true).format();
        const end = CalendarDateService.time(range.end, { base: day, nextDayIfMidnight: true }).tz(currentDates.timeZone, true).format();

        return {
          id: start,
          display: 'background',
          start: start,
          end: end,
        };
      });
    });

    return availability;
  }, [ currentDates?.startStr, currentDates?.endStr, currentDates?.timeZone, availabilityTemplate, overrides, liveAvailabilityData ]);

  const icsConflictsSet = React.useMemo(() => new Set(icsConflicts.map(c => c.appointment.id)), [ icsConflicts ]);

  const appointmentEvents = React.useMemo(() => {
    return events.map(event => {
      const start = CalendarDateService.date(event.start);
      const end = CalendarDateService.date(event.end);

      return {
        ...event,
        extendedProps: {
          ...event.extendedProps,
          duration: end.diff(start, 'minutes'),
          isICSConflict: icsConflictsSet.has(event.id),
          inOnAvailability: availabilityEvents.some(availability => {
            const isStartInAvailability = start.isSameOrAfter(availability.start as string) && start.isBefore(availability.end as string);

            if (isStartInAvailability) {
              return isStartInAvailability;
            }

            const isEndInAvailability = end.isAfter(availability.start as string) && end.isBefore(availability.end as string);

            return isEndInAvailability;
          })
        }
      };
    });
  }, [ events, availabilityEvents, icsConflictsSet ]);

  const customEmitter = React.useMemo(() => {
    return new EventEmitter();
  }, []);

  const [ calendarRef, setCalendarRef ] = React.useState<FullCalendar>();
  const [ selectedIcsEvent, setSelectedIcsEvent ] = React.useState<{ el: HTMLElement; event: ICSEvent }>();
  const { classes } = useStyles();

  React.useEffect(() => {
    if (!calendarRef) {
      return;
    }

    const calendarApi = calendarRef.getApi();

    props.setCalendarApi(calendarApi);
    scrollCalendarToCurrentTime(calendarApi, timezone);
  }, [ calendarRef ]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleEventClick(clickInfo: EventClickArg) {
    if (clickInfo.event.extendedProps.variant === 'ics') {
      setSelectedIcsEvent({ el: clickInfo.el, event: clickInfo.event.extendedProps.icsEvent });
    } else {
      props.setSelectedEvent(clickInfo.event);
      props.openModal('details');
    }
  }

  const [ selectedInfo, setSelectedInfo ] = React.useState<{ startStr: string; endStr: string }>();
  const [ popoverMenuTarget, showPopover, _closePopover ] = useMousePositionedPopover();
  const closePopover = React.useCallback(() => {
    _closePopover();
    setSelectedInfo(undefined);
  }, []);

  const [ calendarRoot, setCalendarRoot ] = React.useState<HTMLDivElement | null>();

  function editAvailability(date: DateObject) {
    const weekdayKey = WeekDay[date.day()] as WeekDayKeys;
    const templateAvailability = availabilityTemplate?.[weekdayKey];
    const overrideAvailability = overrides[date.format(DateFormats.ISO_DATE)];
    const _availability = overrideAvailability || templateAvailability || [];
    const availability = _availability.map((a, i) => ({ id: i, ...a }));

    setEditingAvailabilityTemplate({
      weekday: weekdayKey,
      availability: availability,
      date: date,
      defaultAvailability: availabilityTemplate?.[weekdayKey],
      isAddingOverride: _availability === templateAvailability,
    });
  }

  function handleDayCellClassName(dayCell: DayCellContentArg) {
    if (dayCell.isPast) {
      return 'day-cell__past';
    }

    if (dayCell.isToday) {
      return 'day-cell__today';
    }

    return '';
  }

  const renderEvent = React.useCallback((arg: EventContentArg) => {
    const timezone = arg.view.getOption('timeZone') as string;

    if (arg.event.display === 'background') {
      const start = CalendarDateService.date(arg.event.start);
      const elevation = (
        editingAvailabilityTemplate?.weekday !== undefined
        && start.tz(timezone).day() === WeekDay[editingAvailabilityTemplate.weekday]
      );

      return (
        <EventItem
          id={arg.event.id}
          showFullRange
          elevation={elevation}
          startTime={arg.event.start}
          endTime={arg.event.end}
          timezone={timezone}
          className={classes.cursorInherit}
          variant="availability"
        />
      );
    }

    if (arg.event.display === 'ics') {
      return (
        <EventItem
          id={arg.event.id}
          title={arg.event.title ?? ''}
          showFullRange
          startTime={arg.event.start}
          endTime={arg.event.end}
          timezone={timezone}
          variant="ics"
          allDay={arg.event.allDay}
          elevation={selectedIcsEvent?.event.id === arg.event.id}
        />
      );
    }

    if (arg.event.id === NEW_EVENT_ID) {
      return (
        <EventItem
          key={arg.event.startStr}
          id={NEW_EVENT_ID}
          elementRef={props.newEventAnchorRef}
          isStart={arg.isStart}
          isEnd={arg.isEnd}
          title={arg.event.title || '...'}
          startTime={arg.event.start}
          endTime={arg.event.end}
          timezone={timezone}
          variant={arg.event.extendedProps.isICSConflict ? 'appointmentWarning' : 'appointment'}
          leftSpacing
        />
      );
    }

    return (
      <EventItemCreated {...arg} />
    );
  }, [ editingAvailabilityTemplate?.weekday, selectedIcsEvent ]);

  const getCalendarNow = () => {
    const tz = calendarApi?.getOption('timeZone');

    if (props.now) {
      if (props.now instanceof Function) {
        return CalendarDateService
          .date(props.now() as string)
          .tz(tz, true);
      }

      return CalendarDateService
        .date(props.now as string)
        .tz(tz, true);
    }

    return CalendarDateService.date()
      .tz(tz);
  };

  React.useImperativeHandle(_calendarRef, () => {
    return {
      customEmitter,
      editAvailability,
      calendarApi,
    };
  });

  const eventSources = React.useMemo(() => {
    return [
      {
        id: 'availability',
        display: 'background',
        events: availabilityEvents,
      },
      {
        id: 'availability',
        display: 'ics',
        events: icsEvents,
      },
      {
        id: 'appointments',
        events: appointmentEvents,
      },
    ] as EventSourceInput[];
  }, [ availabilityEvents, icsEvents, appointmentEvents ]);

  const allEventsSources = React.useMemo(() => {
    return props.isCalendarLoading ? [] : eventSources;
  }, [ props.isCalendarLoading, eventSources ]);

  const snapDuration = React.useMemo(() => {
    if (me.medicalRole === ProviderMedicalRole.psychiatrist) {
      return '00:05:00';
    }
    return '00:15:00';
  }, []);

  const findAvailableSlot = (start: DateObject): TimeWindow | false => {
    if (!me.medicalRole) {
      return false;
    }

    const durations = {
      [ProviderMedicalRole.therapist]: [ 60, 45, 30 ],
      [ProviderMedicalRole.psychiatrist]: [ 30, 20, 15 ],
    }[me.medicalRole];
    const slotMinutesDuration = {
      [ProviderMedicalRole.therapist]: 15,
      [ProviderMedicalRole.psychiatrist]: 5,
    }[me.medicalRole];

    for (const duration of durations) {
      let localStart = start;
      let localEnd = localStart.add(duration, 'minutes');
      let isAllowed = props.checkSlotAvailability(localStart, localEnd, slotMinutesDuration);
      const checkLoops = (duration / slotMinutesDuration) - 1;

      for (let i = 0; i < checkLoops; i++) {
        if (isAllowed) break;

        localStart = localStart.subtract(slotMinutesDuration, 'minutes');
        localEnd = localStart.add(duration, 'minutes');

        isAllowed = props.checkSlotAvailability(localStart, localEnd, slotMinutesDuration);
      }

      if (isAllowed === true) {
        return { start: localStart, end: localEnd };
      }
    }

    return false;
  };

  const handleDateClick = React.useCallback((e: { dateStr: string }) => {
    const start = CalendarDateService.date(e.dateStr).tz(timezone).utc();
    const slotAvailable = findAvailableSlot(start);
    const now = getCalendarNow();

    if (props.availabilityEditing && start.isSameOrAfter(now)) {
      const clickedOnAvailability = availabilityEvents.find(v => (
        start.isSameOrAfter(CalendarDateService.date(v.start))
        && start.isBefore(CalendarDateService.date(v.end))
      ));

      if (clickedOnAvailability && !allowToCreateAppointments) {
        return editAvailability(CalendarDateService.date(clickedOnAvailability.start).tz(timezone));
      }
      if (clickedOnAvailability && slotAvailable) {
        setSelectedInfo({
          startStr: slotAvailable.start.format(),
          endStr: slotAvailable.end.format()
        });
        showPopover();
      } else if (slotAvailable && allowToCreateAppointments) {
        setNewEvent?.({
          id: NEW_EVENT_ID,
          start: slotAvailable.start.format(),
          end: slotAvailable.end.format()
        });
      }
    }

    if (!slotAvailable) {
      document.body.classList.add('fc-not-allowed');
      setTimeout(() => {
        document.body.classList.remove('fc-not-allowed');
      }, 100);
    }
  }, [ timezone, findAvailableSlot, getCalendarNow, props.availabilityEditing, availabilityEvents, showPopover, setNewEvent ]);

  const conflictsCount = icsConflicts?.length ?? 0;

  const hasAllDayInRange = React.useMemo(() => {
    if (!calendarApi?.view.activeStart) {
      return false;
    }

    const start = CalendarDateService.date(calendarApi.view.activeStart);
    const end = CalendarDateService.date(calendarApi.view.activeEnd);

    return icsEvents.some(e => {
      return e.allDay && (
        CalendarDateService.inRange(e.start, { start, end })
        || CalendarDateService.inRange(e.end, { start, end })
      );
    });
  }, [ icsEvents, calendarApi?.view.activeStart, calendarApi?.view.activeEnd ]);


  return (
    <UpliftCalendarStylesWrapper ref={setCalendarRoot}>
      <div className={allowToCreateAppointments ? classes.calendarContent : classes.calendarContentDisabled}>
        {props.isCalendarLoading && (
          <div className={classes.calendarLoader}>
            <CircularProgress />
          </div>
        )}

        <FullCalendar
          height="100%"
          ref={React.useCallback((e: FullCalendar) => setCalendarRef(e ?? undefined), [])}
          scrollTime={undefined}
          plugins={[ dayGridPlugin, timeGridPlugin, interactionPlugin, UpliftTZPlugin ]}
          titleFormat={titleFormatter}
          nowIndicatorContent={() => {
            return renderDayNotAllowedArea(getCalendarNow());
          }}
          eventOrder={(a: any, b: any) => {
            if (
              a.extendedProps.isICSConflict
              || a.id === NEW_EVENT_ID
            ) {
              return 1;
            }

            if (a.extendedProps.duration < b.extendedProps.duration) {
              return 1;
            }

            return -1;
          }}
          headerToolbar={{
            left: 'today prev,next title',
            center: '',
            right: conflictsCount
              ? 'icsConflicts dayWeekDropdown settings'
              : 'dayWeekDropdown settings',
          }}
          customButtons={{
            today: {
              text: 'Today',
              click: () => {
                calendarApi?.today();
                customEmitter.emit('today');
              },
            },
            settings: {
              text: (
                <AvailabilitySettings />
              ) as unknown as string,
            },
            icsConflicts: {
              text: (
                <ICSConflictsButton count={conflictsCount} />
              ) as unknown as string,
              click: () => {
                onICSConflictsOpen?.();
              }
            },
            dayWeekDropdown: {
              text: (
                <CalendarTypeSelect
                  value={calendarType}
                  onChange={ct => {
                    setCalendarType(ct);
                    calendarApi?.changeView(ct.value);
                  }}
                />
              ) as unknown as string,
            },
          }}
          slotLabelContent={arg => {
            return CalendarDateService.date(arg.date).tz(arg.view.getOption('timeZone') as string).format('h A');
          }}
          key={timezone}
          timeZone={timezone}
          dayHeaderContent={(arg) => <DayHeaderContent arg={arg} overrides={overrides} />}
          initialView={calendarType.value}
          editable={false}
          selectable={false}
          selectMirror={false}
          viewDidMount={(arg) => {
            const frame = arg.el.querySelector('.fc-timegrid-axis-frame');

            if (frame) {
              frame.textContent = CalendarDateService.date().tz(arg.view.getOption('timeZone') as string).format('z');
            }
          }}
          dayMaxEvents={true}
          nowIndicator={true}
          weekends={true}
          allDaySlot={hasAllDayInRange}
          eventResizableFromStart={false}
          snapDuration={snapDuration}
          progressiveEventRendering={false}
          eventContent={renderEvent}
          eventSources={allEventsSources}
          datesSet={setCurrentDates}
          eventAllow={React.useCallback((dropInfo) => {
            return CalendarDateService.isTodayOrFutureDay(dropInfo.startStr);
          }, [])}
          dateClick={handleDateClick}
          dayCellClassNames={handleDayCellClassName}
          eventClick={handleEventClick}
          now={props.now}
        />
      </div>
      <ClosableMenu
        open={!!popoverMenuTarget}
        onClose={closePopover}
        anchorReference="anchorPosition"
        anchorPosition={popoverMenuTarget}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <ClosableMenuItem
          disabled={!selectedInfo}
          onClick={() => {
            if (selectedInfo) {
              setTimeout(() => {
                setNewEvent?.({
                  id: NEW_EVENT_ID,
                  start: selectedInfo.startStr,
                  end: selectedInfo.endStr,
                });
              }, 1);
            }
          }}
        >
          Add session
        </ClosableMenuItem>
        <ClosableMenuItem
          onClick={() => {
            selectedInfo && editAvailability(CalendarDateService.date(selectedInfo.startStr).tz(timezone));
          }}
        >
          Edit availability
        </ClosableMenuItem>
      </ClosableMenu>
      {calendarRoot && editingAvailabilityTemplate && (
        <EditingAvailabilityDayPopup
          prepend={(
            <Box mb={3}>
              <Alert severity="info">
                <Box display="flex" style={{ lineHeight: '1.5rem' }}>
                  Previously booked sessions will not be canceled
                </Box>
              </Alert>
            </Box>
          )}
          onLiveChange={setLiveAvailabilityData}
          calendarRoot={calendarRoot}
          warningOnLessThenHour={me.medicalRole === ProviderMedicalRole.therapist}
          editingAvailabilityTemplate={editingAvailabilityTemplate}
          setEditingAvailabilityTemplate={setEditingAvailabilityTemplate}
          onAvailabilityChange={async (ranges, date) => {
            if (!editingAvailabilityTemplate?.weekday || !date) return;

            await props.onAvailabilityDateOverride?.(date, ranges);
          }}
        />
      )}
      <ICSEventDetailsPopup
        anchorEl={selectedIcsEvent?.el}
        event={selectedIcsEvent?.event}
        onClose={() => setSelectedIcsEvent(undefined)}
      />
    </UpliftCalendarStylesWrapper>
  );
});
